import React from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { ChefHat, ShoppingCart, TrendingDown, CheckCircle } from 'lucide-react';

function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useStore((state) => state.user);
  React.useEffect(() => { if (user) navigate('/dashboard'); }, [user]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="container mx-auto px-4 py-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="bg-primary rounded-full p-2">
            <ChefHat className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-heading font-bold text-primary">FamilyPlate</span>
        </div>
        <div className="flex gap-4">
          <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn">
            {t('nav.login')}
          </Button>
          <Button className="bg-secondary hover:bg-secondary-hover text-white rounded-full" onClick={() => navigate('/signup')} data-testid="nav-signup-btn">
            {t('nav.signup')}
          </Button>
        </div>
      </nav>

      <section className="container mx-auto px-4 py-12 sm:py-20 text-center" data-testid="hero-section">
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-heading font-bold text-[#2C3E2D] mb-4 sm:mb-6 max-w-4xl mx-auto leading-tight">
          {t('landing.hero_title')}
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-[#5F6F60] mb-6 sm:mb-10 max-w-2xl mx-auto">
          {t('landing.hero_subtitle')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary-hover text-white text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all"
            onClick={() => navigate('/signup')}
            data-testid="hero-cta-primary"
          >
            {t('landing.cta_primary')}
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="text-lg px-8 py-6 rounded-full border-2 border-primary text-primary hover:bg-primary/10"
            data-testid="hero-cta-secondary"
          >
            {t('landing.cta_secondary')}
          </Button>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow" data-testid="feature-card-menu">
            <div className="bg-primary/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
              <ChefHat className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-3 text-[#2C3E2D]">{t('landing.feature_menu')}</h3>
            <p className="text-[#5F6F60]">{t('landing.feature_menu_desc')}</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow" data-testid="feature-card-shopping">
            <div className="bg-secondary/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
              <ShoppingCart className="w-8 h-8 text-secondary" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-3 text-[#2C3E2D]">{t('landing.feature_shopping')}</h3>
            <p className="text-[#5F6F60]">{t('landing.feature_shopping_desc')}</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow" data-testid="feature-card-budget">
            <div className="bg-accent/20 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
              <TrendingDown className="w-8 h-8 text-[#3D4035]" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-3 text-[#2C3E2D]">{t('landing.feature_budget')}</h3>
            <p className="text-[#5F6F60]">{t('landing.feature_budget_desc')}</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow" data-testid="feature-card-halal">
            <div className="bg-success/20 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-3 text-[#2C3E2D]">{t('landing.feature_halal')}</h3>
            <p className="text-[#5F6F60]">{t('landing.feature_halal_desc')}</p>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-primary to-[#81B29A] py-20 mt-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">{t('landing.bottom_title')}</h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">{t('landing.bottom_subtitle')}</p>
          <Button 
            size="lg" 
            className="bg-white text-primary hover:bg-gray-100 text-lg px-8 py-6 rounded-full shadow-lg"
            onClick={() => navigate('/signup')}
            data-testid="cta-bottom-btn"
          >
            {t('landing.cta_primary')}
          </Button>
        </div>
      </section>

      <footer className="container mx-auto px-4 py-12 text-center text-gray-600">
        <p>&copy; 2026 FamilyPlate</p>
      </footer>
    </div>
  );
}

export default LandingPage;
